import { HttpCancelError } from '@cian/peperrors/shared';
import { TDeviceType } from '@cian/ui-kit';
import { equals } from 'ramda';

import { assertJsonQueryValidity } from './helpers';
import { trackNothingFound } from './tracking';
import { EFetchMapDataActionType, IFetchMapDataSucceedParameters } from './types';
import { resetTerms } from '../../../../packages/JsonQuery';
import { IJsonQuery } from '../../../../packages/api-models/common/json_query';
import { removeAllTags } from '../../../common/actions/filters';
import { justChangeJsonQuery } from '../../../common/actions/filters/justChangeJsonQuery';
import {
  closeAndResetListingModal,
  EListingModalMode,
  EOffersFetchingMode,
  setActiveOfferId,
  toggleModal,
} from '../../../common/actions/listingModal';
import { removeOffers, setPage } from '../../../common/actions/listingOffers';
import { searchWithNonGeoParameters } from '../../../common/actions/nonGeoParameters';
import { closeSearchResultsSidebar, openSearchResultsSidebar } from '../../../common/actions/searchResults';
import { mapResponse200 } from '../../../common/mappers/map/mapResponse200';
import { mapResponse400 } from '../../../common/mappers/map/mapResponse400';
import { fetchClusters as requestFetchClusters } from '../../../common/services/clusters';
import { TThunkAction } from '../../../common/types/redux';
import { mapBBoxBoundsToBBox, queryStringWithMapBounds } from '../../../common/utils/mapBounds';
import { actionGenerator } from '../../../common/utils/redux/actionGenerator';
import { getVisibleTiles } from '../../../common/utils/tiles';
import { trackPinLoadSuccessDebounced } from '../../tracking/trackPinLoadSuccess';
import { trackPrematurePinLoad } from '../../tracking/trackPrematurePinLoad';
import { finishDrawing } from '../drawing';

const setStatusLoading = actionGenerator<EFetchMapDataActionType.Loading>(EFetchMapDataActionType.Loading);
const setStatusSucceed = actionGenerator<EFetchMapDataActionType.Succeed, IFetchMapDataSucceedParameters>(
  EFetchMapDataActionType.Succeed,
);
const setStatusFailed = actionGenerator<EFetchMapDataActionType.Failed>(EFetchMapDataActionType.Failed);
let prevBounds: [YMaps.TCoord, YMaps.TCoord] | undefined;
let loading: boolean;
let prevFetchStartTimestamp: number;

interface IFetchMapDataParameters {
  updateBounds?: boolean;
  deviceType?: TDeviceType;
  shouldCloseListing?: boolean;
}

export function fetchMapData({
  updateBounds = false,
  deviceType,
  shouldCloseListing,
}: IFetchMapDataParameters = {}): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const {
      custom: { subdomain },
      logger,
    } = context;
    const {
      queryString,
      map: { mapBounds },
      filters: { jsonQuery },
      results: { jsonQuery: resultsJsonQuery },
    } = getState();

    const { bounds, zoom: currentZoom } = mapBounds;
    const isFirstRun = Boolean(!prevFetchStartTimestamp);
    const isBoundsEquals = updateBounds ? false : equals(bounds, prevBounds);
    prevBounds = bounds;

    if (!bounds || !currentZoom) {
      logger.error('Either bounds or zoom is undefined', { domain: 'actions/fetchMapData' });

      return;
    }

    const clean = !equals(jsonQuery, resultsJsonQuery);

    const visibleTiles = getVisibleTiles(bounds, currentZoom);

    if (!clean && isBoundsEquals) {
      logger.debug('Unfortunately we tried to fetch already loaded tiles', {
        domain: 'actions/fetchMapData',
      });

      return;
    }

    const currentFetchStartTimestamp = Date.now();
    // если предыдущий запрос не завершился, прошло более 2сек, и пользователь не дождавшись инициировал сл запрос,
    // то отправляем аналитическое событие о долгом запросе
    if (loading && currentFetchStartTimestamp - prevFetchStartTimestamp > 2000) {
      trackPrematurePinLoad({
        jsonQuery,
        deviceType,
        timeInMilliseconds: currentFetchStartTimestamp - prevFetchStartTimestamp,
        startTimestamp: currentFetchStartTimestamp,
        queryString,
      });
    }
    prevFetchStartTimestamp = currentFetchStartTimestamp;

    loading = true;
    dispatch(setStatusLoading());

    try {
      const bbox = updateBounds ? undefined : mapBBoxBoundsToBBox({ bounds });
      const zoomToSend = updateBounds ? undefined : currentZoom;
      /*
       * отправляем queryString, чтобы после ответа бэка не терялись utm-метки из урлы
       * подробности в задаче https://jira.cian.tech/browse/CD-191441
       */
      const mapResponse = await requestFetchClusters(context, {
        subdomain,
        params: { zoom: zoomToSend, bbox, jsonQuery, queryString },
        mappers: {
          200: mapResponse200(currentZoom),
          400: mapResponse400,
        },
      });

      // Если уже был сделан следующий запрос, то этот запрос останавливаем
      // AbortController внутри api service для этого недостаточно
      if (prevFetchStartTimestamp && prevFetchStartTimestamp > currentFetchStartTimestamp) {
        return;
      }

      assertJsonQueryValidity(logger, jsonQuery, mapResponse.jsonQuery as unknown as IJsonQuery);

      dispatch(
        setStatusSucceed({
          ...mapResponse,
          jsonQuery,
          queryString: queryStringWithMapBounds(mapResponse.queryString, mapBounds),
          clean,
          tiles: visibleTiles,
          updateBounds,
        }),
      );

      trackPinLoadSuccessDebounced({
        jsonQuery,
        deviceType,
        timeInMilliseconds: Date.now() - currentFetchStartTimestamp,
        startTimestamp: currentFetchStartTimestamp,
        success: true,
        queryString,
      });

      if (mapResponse.offersCount) {
        dispatch(closeSearchResultsSidebar());
      }

      if (!mapResponse.offersCount && mapResponse.nonGeoOffersCount) {
        dispatch(finishDrawing());
        dispatch(removeAllTags());
        dispatch(searchWithNonGeoParameters());
      } else if (!mapResponse.offersCount && !isFirstRun) {
        dispatch(removeOffers());
        dispatch(setActiveOfferId(null));
        dispatch(justChangeJsonQuery({ nextJsonQuery: resetTerms(['page'])(jsonQuery) }));
        dispatch(setPage(1));
        dispatch(
          toggleModal({
            open: true,
            mode: EListingModalMode.HALF_SCREEN,
            offersFetchingMode: EOffersFetchingMode.NONE,
          }),
        );
        dispatch(openSearchResultsSidebar());
        trackNothingFound(deviceType);
      } else if (!mapResponse.offersCount) {
        dispatch(openSearchResultsSidebar());
        dispatch(
          toggleModal({
            open: true,
            mode: EListingModalMode.HALF_SCREEN,
            offersFetchingMode: EOffersFetchingMode.NONE,
          }),
        );
      } else if (shouldCloseListing && !isFirstRun) {
        dispatch(closeAndResetListingModal());
      }
    } catch (error) {
      trackPinLoadSuccessDebounced({
        jsonQuery,
        deviceType,
        timeInMilliseconds: Date.now() - currentFetchStartTimestamp,
        startTimestamp: currentFetchStartTimestamp,
        success: false,
        queryString,
      });

      // Проверяем отмену запроса: HttpCancelError или AbortError (от AbortController)
      const isAbortError = error instanceof Error && error.name === 'AbortError';
      if (error instanceof HttpCancelError || isAbortError) {
        return;
      }

      logger.error(error);

      dispatch(removeOffers());
      dispatch(setActiveOfferId(null));
      dispatch(justChangeJsonQuery({ nextJsonQuery: resetTerms(['page'])(jsonQuery) }));
      dispatch(setPage(1));
      dispatch(
        toggleModal({
          open: true,
          mode: EListingModalMode.HALF_SCREEN,
          offersFetchingMode: EOffersFetchingMode.NONE,
        }),
      );
      dispatch(openSearchResultsSidebar());
      trackNothingFound(deviceType);

      dispatch(setStatusFailed());
    } finally {
      loading = false;
    }
  };
}
